import React, { useEffect, useMemo, useState } from 'react';
import { boreholesApi } from '../../lib/api';
import {
  columnGeometry, loadColorOverrides, logPolyline, sectionBuffer, sectionLineParam,
} from '../../lib/boreholes';

/**
 * One borehole drawn as a lithology column inside a section SVG, with its first log
 * curve beside it. `geom` comes from lib/boreholes.columnGeometry.
 */
export default function BoreholeColumn({ hole, geom, logPoints = '', logName = '' }) {
  if (!geom) return null;
  const { left, width, top, rects } = geom;
  const bottom = rects.length ? rects[rects.length - 1].y + rects[rects.length - 1].h : top;
  const offset = Number.isFinite(hole.offset) ? ` · ${hole.offset >= 0 ? 'L' : 'R'} ${Math.abs(hole.offset).toFixed(0)} m` : '';
  return (
    <g data-testid="borehole-column">
      <title>{`${hole.name}${offset}`}</title>
      {rects.map((r, i) => (
        <rect key={i} x={left} y={r.y} width={width} height={Math.max(0.5, r.h)} fill={r.color}
          stroke="#0b1220" strokeWidth={0.4}>
          <title>{`${hole.name}: ${r.lithology || 'unknown'} ${r.depth_from}-${r.depth_to} m`}</title>
        </rect>
      ))}
      <rect x={left} y={top} width={width} height={Math.max(0, bottom - top)} fill="none" stroke="#e6edf3" strokeWidth={0.8} />
      {logPoints && (
        <>
          <polyline points={logPoints} fill="none" stroke="#fbbf24" strokeWidth={1} />
          <text x={left + width + 2} y={bottom + 9} fill="#fbbf24" fontSize={7}>{logName}</text>
        </>
      )}
      <text x={left + width / 2} y={top - 3} fill="#e6edf3" fontSize={8} textAnchor="middle">{hole.name}</text>
    </g>
  );
}

/**
 * Boreholes near a georeferenced section, drawn into SectionView's filled section through
 * its `renderOverlay` injection point. Receives the section's scales from SectionView:
 * { entry, px, py, x0, x1, topo, surfaceMax, nearestSurface, mT, plotH }.
 * Renders nothing for a model without a WGS84 line (not georeferenced).
 */
export function BoreholeSectionOverlay({ entry, px, py, x0, x1, topo, surfaceMax, nearestSurface, mT, plotH }) {
  const line = sectionLineParam(entry);
  const [holes, setHoles] = useState([]);
  useEffect(() => {
    if (!line) { setHoles([]); return undefined; }
    let active = true;
    boreholesApi.nearLine({ line, buffer: sectionBuffer(x0, x1), include_logs: true })
      .then(res => { if (active) setHoles(res.data?.boreholes || []); })
      .catch(() => { if (active) setHoles([]); }); // no boreholes table yet, or none nearby
    return () => { active = false; };
  }, [line, x0, x1]);

  const overrides = useMemo(() => loadColorOverrides(), []);
  if (!holes.length) return null;
  const yTop = mT;
  const yBottom = mT + plotH;
  return (
    <g data-testid="borehole-overlay">
      {holes.map((h) => {
        const x = x0 + (h.fraction ?? 0) * (x1 - x0);
        const geom = columnGeometry({ x, intervals: h.intervals || [], px, py, topo, surfaceMax,
          surfaceAt: nearestSurface, yTop, yBottom, width: 10, overrides });
        const log = (h.logs || [])[0];
        const logPoints = log ? logPolyline(log.samples, { left: geom.left + geom.width + 2, width: 24,
          depthToY: (d) => py(geom.shift + d), yTop, yBottom }) : '';
        return <BoreholeColumn key={h.id} hole={h} geom={geom} logPoints={logPoints} logName={log?.name || ''} />;
      })}
    </g>
  );
}
